/**
 * Words for the server's end-of-day decision codes (B-W3, B-W17 residual d). Pure; no I/O.
 *
 * The server answers with stable codes (decide_rollover_conversion REFUSED causes, authorize_ticket_overtime errors,
 * the options' disabledReason). A person must never be shown a raw code such as APPOINTMENT_CANNOT_CARRY. Unknown
 * codes fall back to a readable form of the code itself (never an empty message).
 */

const REFUSAL_MESSAGES: Readonly<Record<string, string>> = {
  APPOINTMENT_CANNOT_CARRY:
    'This ticket is a customer appointment. A protected appointment is never carried to another day — reassign it, or reschedule it with the customer.',
  WAITER_CANNOT_CARRY:
    'This ticket is a Waiter. A waiting customer\'s work is never carried to another day — reassign it, or reschedule it with the customer.',
  BREAKS_DUE_OUT_PROMISE:
    'Carrying this work would break the customer\'s due-out promise. Change the promise with the customer first, or choose another option.',
  NO_CAPACITY_IN_HORIZON:
    'There is no capacity to carry this work in the planning horizon. Choose another option.',
  OVERTIME_AUTHORITY_REQUIRED:
    'Your role cannot authorize overtime in this shop. Ask someone who can, or choose another option.',
  OVERTIME_EXCEEDS_MAXIMUM: 'That is more overtime than can be authorized for one ticket on one day.',
  NOT_PAST_BOUNDARY: 'The overtime must end after the end of day.',
  DATE_IN_PAST: 'Overtime cannot be authorized for a day that has passed.',
  REASON_REQUIRED: 'Overtime needs a reason.',
  STARTED_WORK_CARRY_THROUGH_EOD_FLOW: 'Started work is carried through the end-of-day carry dialog.',
};

const DISABLED_REASON_MESSAGES: Readonly<Record<string, string>> = {
  ticket_not_live: 'the ticket is no longer active',
  waiter_cannot_carry: 'a Waiter is never carried to another day',
  appointment_cannot_carry: 'a customer appointment is never carried to another day',
  breaks_due_out_promise: 'it would break the due-out promise',
  no_capacity_in_horizon: 'no capacity in the planning horizon',
  overtime_exceeds_maximum: 'more overtime than can be authorized',
  no_overtime_needed: 'no overtime is needed',
  not_authorized_for_overtime: 'your role cannot authorize overtime in this shop',
  // OCT20 WS-ROLL (FD-11): the question is about a day that has already ended.
  day_already_ended: 'the last approved day has already ended',
};

const readable = (code: string) => code.replace(/_/g, ' ').toLowerCase();

/** A REFUSED cause or an RPC error message (which starts with the code, e.g. "OVERTIME_AUTHORITY_REQUIRED: …"). */
export function describeDecisionRefusal(causeOrMessage: string | null | undefined): string {
  const raw = String(causeOrMessage ?? '').trim();
  if (!raw) return 'The server refused this decision.';
  const code = /^([A-Z][A-Z0-9_]+)\b/.exec(raw)?.[1] ?? raw;
  return REFUSAL_MESSAGES[code] ?? (code === raw && /^[A-Z0-9_]+$/.test(raw) ? `Refused: ${readable(raw)}.` : raw);
}

/** Why an option is unavailable, in words. */
export function describeDisabledReason(reason: string | null | undefined): string {
  if (!reason) return '';
  return DISABLED_REASON_MESSAGES[reason] ?? readable(reason);
}
